import { useEffect, useRef, useState } from "react";
import "./WorkFlowRequestDetail.scss";

import { useNavigate, useParams } from "react-router-dom";

import CommentCard from "../../components/CommentSection/CommentCard/CommentCard";
import { getFileIcon } from "../../utils/Helper";
import LtrDetailTab from "../../components/LtrDetailTab/LtrDetailTab";
import ApiService from "../../services/apiService";
import URLS from "../../urls/Urls";
import { IComment } from "../../models/Auction";
import { toast } from "react-toastify";
import { toastOptions } from "../../utils/toast";
import {
  FormDetail,
  OptionsSchema,
  QuestionDetail,
  VendorFormResponse,
} from "../../models/VendorForm";
import CustomQuestionFormResponse from "../../components/CustomQuestionFormResponse/CustomQuestionFormResponse";
import FlowPlayground from "../../components/FlowPlayground/FlowPlayground";
import { WorkFlowStage } from "../../models/Workflow";
import {
  Connection,
  Edge,
  Node,
  OnEdgesChange,
  OnNodesChange,
  Position,
  useEdgesState,
  useNodesState,
} from "reactflow";
import {
  INIT_WORKFLOW_INFO,
  IWorkFlowBasicInfo,
  WorkFlowContext,
} from "../../context/workflow-context";
import { IWorkFlowRequestInfo } from "../../models/WorkflowRequest";
import XetModal from "../../components/shared/xet-modal/XetModal";
import RequestStageDetail from "../../components/RequestStageDetail/RequestStageDetail";

const WorkFlowRequestDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [status, setStatus] = useState("overview");
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [newComment, setNewComment] = useState("");
  const [formUpdated, setFormUpdated] = useState(false);
  const messagesEndRef = useRef<null | HTMLDivElement>(null);
  const [comments, setComments] = useState<IComment[]>([]);
  const [formDetails, setFormDetails] = useState<FormDetail | null>(null);
  const [formResponse, setFormResponse] = useState<VendorFormResponse>();
  const [fileMap, setFileMap] = useState<{ [key: number]: File[] }>({});
  const [formData, setFormData] = useState<FormData>(new FormData());
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [info, setInfo] = useState<IWorkFlowRequestInfo>({
    name: "",
    form_id: -1,
    workflow_id: -1,
  });
  const [nodeDetails, setNodeDetails] = useState<{
    [key: number]: WorkFlowStage;
  }>({});
  const [stageDetail, setStageDetail] = useState(-1);

  useEffect(() => {
    if (status === "form") {
      getFormDetail();
    } else if (status === "comments") {
      getComments();
    } else if (status === "overview") {
      getWorkFlowDetails();
    }
  }, [status]);

  useEffect(() => {
    const newFormData = new FormData();

    Object.keys(fileMap).forEach((questionId) => {
      const files = fileMap[parseInt(questionId)];
      files.forEach((file) => {
        newFormData.append("questions[]", questionId);
        newFormData.append("files[]", file);
        newFormData.append("fileNames[]", file.name);
      });
    });

    setFormData(newFormData);
  }, [fileMap]);

  const getWorkFlowDetails = async () => {
    if (id) {
      try {
        const { data } = await ApiService().client.get(
          URLS.WORKFLOW.REQUEST_WORKFLOW_DETAIL(parseInt(id)),
        );
        if (data && data.workflow) {
          const details: { [key: number]: WorkFlowStage } = {};
          setInfo(data.workflow.info);
          const diff = data.workflow.nodes.filter((node: any) => node.start)[0]
            .position;
          setNodes(
            data.workflow.nodes.map((node: any) => {
              details[node.id] = node.details;
              return {
                id: node.id.toString(),
                sourcePosition: Position.Right,
                targetPosition: Position.Left,
                position: {
                  x: node.position.x - diff.x + 20,
                  y: node.position.y - diff.y + 20,
                },
                type: "status",
                data: { id: node.id, ...node.details },
              };
            }),
          );
          setEdges(
            data.workflow.edges.map((edge: any) => {
              return {
                id: edge.id,
                source: edge.source,
                sourceHandle: edge.source_handle,
                target: edge.target,
                targetHandle: edge.target_handle,
                type: edge.type,
                data: { label: edge.label },
              };
            }),
          );
          setNodeDetails(details);
        }
      } catch (error) {
        console.error("Error fetching workflow details");
      }
    }
  };

  const getFormDetail = async () => {
    const { data } = await ApiService().client.get(
      URLS.WORKFLOW.REQUEST_FORM_DETAIL(Number(id)),
    );
    setFormDetails(data.form_detail);
    const questions: QuestionDetail[] = data.form_detail.questions;
    const initialResponses = questions.map((question) => ({
      question_id: question.id,
      input_type: question.input_type,
      responses: [],
    }));

    const initialFormResponse: VendorFormResponse = {
      vendor_form_id: info.form_id,
      responses: initialResponses,
    };

    setFormResponse(initialFormResponse);
  };

  const getComments = async () => {
    try {
      const { data } = await ApiService().client.get(
        URLS.WORKFLOW.COMMENT_LIST(Number(id)),
      );
      setComments(data.data);
    } catch (error) {
      console.error("Error Getting Comment Details", error);
    }
  };

  const handleCommentSubmit = async () => {
    console.log("make api ccall for comment subssion");
    try {
      if (newComment.length <= 0) {
        toast.error("Please add comment", toastOptions);
        return;
      }
      const formData = new FormData();
      formData.append("comment", newComment);
      if (selectedFiles.length > 0) {
        selectedFiles.forEach((file) => {
          formData.append("files[]", file);
          formData.append("displayNames[]", file.name);
        });
      }
      const { data } = await ApiService().client.post(
        URLS.WORKFLOW.ADD_COMMENT(Number(id)),
        formData,
      );
      setComments((prevComments) => [...prevComments, data.data]);
      setNewComment("");
      setSelectedFiles([]);
    } catch (error) {
      console.error("Error Adding Comment ", error);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const files = Array.from(event.target.files);
      setSelectedFiles(files);
    }
  };

  const deleteFile = (file: File) => {
    setSelectedFiles((old) => old.filter((row) => row.name !== file.name));
  };

  const navigateToRequestList = () => {
    navigate("/requests");
  };

  const onQuestionResponseCapture = (
    question: QuestionDetail,
    response: string[] | File[] | OptionsSchema[],
  ) => {
    if (response[0] instanceof File) {
      setFileMap((prevFileMap) => ({
        ...prevFileMap,
        [question.id]: response as File[],
      }));
    } else {
      setFormResponse((prevFormResponse) => {
        if (!prevFormResponse) return undefined;

        const updatedResponses = prevFormResponse.responses.map((res) => {
          if (res.question_id === question.id) {
            return {
              ...res,
              responses: response,
            };
          }
          return res;
        });

        return {
          ...prevFormResponse,
          responses: updatedResponses,
        };
      });
    }
  };

  const handleFormUpdate = async () => {
    formData.append("formResponse", JSON.stringify(formResponse));

    try {
      const { data } = await ApiService().client.put(
        URLS.WORKFLOW.UPDATE_WORKFLOW_VENDOR_FORM(Number(id)),
        formData,
      );
      setFormUpdated(!formUpdated);
    } catch (error) {
      console.error("Error Updating form", error);
    }
  };

  const addNodes = (node: Node) => {};

  const onConnect = (params: Edge | Connection) => {
    const newEdge: Edge = {
      ...params,
      id: `edge-${params.source}-${params.target}`,
    } as Edge;
    setEdges((prev) => [...prev, newEdge]);
  };

  const updateInfo = (key: string, value: any) =>
    setInfo((prev) => ({ ...prev, [key]: value }));

  const updateNodeDetails = (data: WorkFlowStage) => {
    setNodeDetails((prev) => ({ ...prev, [data.id]: data }));
  };

  const saveWorkflow = (_publish: boolean) => {};

  const teamList = [
    { id: 1, name: "Stage 1" },
    { id: 2, name: "Stage 2" },
    { id: 3, name: "Stage 3" },
  ];

  return (
    <div className="workflow-detail-page  full-width flex-column gap-16">
      <div className="upper-bar xetgo-font-tag px-16 py-12 flex-row gap-4">
        <p onClick={navigateToRequestList} className="bold cursor-pointer">
          Requests
        </p>
        <p style={{ color: "#757575" }}> / {info.name}</p>
      </div>
      <div className="breadcrumbs-btns-middle-container flex-row align-items-center justify-content-space-between p-16">
        <div className="breadcrumbs-left-container xetgo-font-tag flex-row align-items-center p-2">
          <p
            onClick={() => {
              setStatus("overview");
            }}
            className={`status flex-row align-items-center justify-content-center px-4 py-6 cursor-pointer ${
              status === "overview" && "active-current-status"
            } `}
          >
            Overview
          </p>
          <p
            onClick={() => {
              setStatus("details");
            }}
            className={`status flex-row align-items-center justify-content-center px-4 py-6 cursor-pointer ${
              status === "details" && "active-current-status"
            } `}
          >
            Details
          </p>
          <p
            onClick={() => {
              setStatus("form");
            }}
            className={`status flex-row align-items-center justify-content-center px-4 py-6 cursor-pointer ${
              status === "form" && "active-current-status"
            } `}
          >
            Form
          </p>
          <p
            onClick={() => {
              setStatus("comments");
            }}
            className={`status flex-row align-items-center justify-content-center px-4 py-6 cursor-pointer ${
              status === "comments" && "active-current-status"
            } `}
          >
            Comments
          </p>
        </div>
      </div>
      <div className="workflow-list-lower-main-container full-width  border-box px-16">
        {status === "overview" && (
          <div className="flow-container p-16 full-height">
            <WorkFlowContext.Provider
              value={{
                nodes,
                edges,
                teamList,
                info: INIT_WORKFLOW_INFO,
                addNodes,
                onConnect,
                updateInfo,
                onNodesChange,
                onEdgesChange,
                nodeDetails,
                updateNodeDetails,
                saveWorkflow,
                setSelectedNode: (id) => setStageDetail(id),
              }}
            >
              <FlowPlayground />
            </WorkFlowContext.Provider>
          </div>
        )}
        {status === "details" && <LtrDetailTab />}
        {status === "form" && formDetails && (
          <div className="full-height flex-column ">
            <div className="form-main-container p-4 border-box full-height flex-1">
              {/* <VendorFormReview
              allQuestions={vendorForm.questions}
              formTitle={vendorForm.title}
              formDescription={vendorForm.description}
              vendorTab={true}
              requestForm={true}
            /> */}
              {formDetails.questions.map((question, questionIndex) => (
                <CustomQuestionFormResponse
                  key={questionIndex}
                  questionDetails={question}
                  onValueChange={onQuestionResponseCapture}
                  disabled={false}
                />
              ))}
            </div>
            <div className="workflow-form-btns-container justify-content-end align-items-end flex-row gap-12 new-form-btn-container py-12 px-16">
              {/* <div
                onClick={handleBackNavigation}
                className="back-btn cursor-pointer xetgo-font-tag flex-row align-items-ccenter justify-content-center px-20 py-8"
              >
                Back
              </div> */}
              <div
                onClick={handleFormUpdate}
                className="back-btn submit-btn cursor-pointer flex-row align-items-ccenter justify-content-center px-20 py-8 xetgo-font-tag"
              >
                Submit
              </div>
            </div>
          </div>
        )}
        {status === "comments" && (
          <div className="comment-section-main-container border-box full-height">
            <div className="comment-section-inner-container p-12 border-box flex-column  justify-content-space-between">
              {comments.length > 0 && (
                <div className="flex-column gap-12 flex-1 overflow-scroll">
                  {comments.length > 0 &&
                    comments.map((comment, index) => (
                      <CommentCard
                        key={comment.id}
                        avatar={comment.avatar}
                        name={comment.name}
                        timestamp={comment.created_at}
                        description={comment.comment}
                        commentFiles={comment.files}
                        user_id={comment.user_id}
                        hideType={true}
                      />
                    ))}

                  <div ref={messagesEndRef} />
                </div>
              )}
              {comments.length == 0 && (
                <div
                  style={{ textAlign: "center" }}
                  className="flex-column gap-40 align-items-center justify-content-center full-height"
                >
                  <img
                    height={184}
                    width={215}
                    src="https://d2k6zobmg5lufr.cloudfront.net/assets%2F20250327120948-Group+%281%29.svg"
                    alt="empty-comment-img"
                  />
                  <p
                    style={{ color: "#33333366" }}
                    className="xetgo-font-sub-heading bold"
                  >
                    No comment yet
                  </p>
                </div>
              )}
              <div className="add-comment-container full-width border-box p-8 flex-column">
                {selectedFiles && (
                  <div className="flex-column">
                    {selectedFiles.map((file: File, index: number) => {
                      return (
                        <div
                          style={{ background: "white" }}
                          className="file-card flex-row justify-content-space-between border-box p-8"
                          key={index}
                        >
                          <div className="file-left flex-row gap-8 align-items-center">
                            <img
                              src={getFileIcon(file)}
                              alt="file-icon"
                              height={16}
                              width={16}
                              className="file-preview"
                            />
                            <p
                              className="xetgo-font-tag file-name bold"
                              style={{ color: "#E1E1E1" }}
                            >
                              |
                            </p>
                            <p
                              className="xetgo-font-tag file-name bolder"
                              style={{ color: "#4A4A4A" }}
                            >
                              {file.name}
                            </p>
                          </div>
                          <div className="flex-row gap-12 align-items-center">
                            <img
                              className="cursor-pointer"
                              src="https://d2k6zobmg5lufr.cloudfront.net/assets%2F20231012084858-close.svg"
                              alt="close"
                              width={16}
                              height={16}
                              onClick={() => deleteFile(file)}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                <div className="full-width border-box xetgo-font-tag comment-value">
                  <textarea
                    style={{ outline: "none" }}
                    className="full-width p-8 border-box xetgo-font-tag"
                    rows={3}
                    onChange={(e) => setNewComment(e.target.value)}
                    value={newComment}
                  ></textarea>
                </div>

                <div className="attach-send-comment-container flex-row align-items-center justify-content-space-between">
                  <input
                    onChange={handleFileChange}
                    id="commentFileInput"
                    name="file"
                    type="File"
                    style={{ display: "none" }}
                    multiple
                  />
                  <label htmlFor="commentFileInput">
                    <img
                      className="cursor-pointer"
                      src="https://d2k6zobmg5lufr.cloudfront.net/assets%2F20240117075035-_WYSIWYG-toolbar-button.svg"
                      alt="attachment-icon"
                    />
                  </label>
                  <div
                    onClick={handleCommentSubmit}
                    className="send-btn xetgo-font-tag px-13 py-5 flex-row align-items-center justify-content-center gap-12 cursor-pointer"
                  >
                    <span>Send</span>{" "}
                    <img
                      height={16}
                      width={16}
                      src="https://d2k6zobmg5lufr.cloudfront.net/assets%2F20241128100505-send.svg"
                      alt="send-icon"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {id && stageDetail !== -1 && (
        <div className="background-container">
          <div className="flex-1" onClick={() => setStageDetail(-1)}></div>
          <RequestStageDetail
            request_id={parseInt(id)}
            stage_id={stageDetail}
          />
        </div>
      )}
    </div>
  );
};

export default WorkFlowRequestDetail;
